import { signInWithEmail, useAuthModalContext } from '@dreamstack/auth'
import { useTranslation } from '@dreamstack/i18n'
import { SimpleFieldError } from '@dreamstack/simple-components/src/lib/SimpleFieldError'
import { cn } from '@dreamstack/util/src/cn'
import type { FunctionComponent } from 'react'
import { useCallback, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { SimpleMuiButton } from '../../layout/SimpleMuiButton'
import { AuthEmailDisplay } from './AuthEmailDisplay'

export type LoginFormData = {
  email: string
}

const StyledForm: React.FC<React.FormHTMLAttributes<HTMLFormElement>> = ({
  className,
  ...rest
}) => (
  <form className={cn('space-y-px-40 md:space-y-px-48', className)} {...rest} />
)

export const Login: FunctionComponent<
  React.PropsWithChildren<unknown>
> = () => {
  const { setActiveStep, email } = useAuthModalContext()
  const t = useTranslation()
  const [errorMessage, setErrorMessage] = useState('')
  const [loading, setLoading] = useState(false)

  const onSubmit = useCallback(async () => {
    setLoading(true)
    const signInResponse = await signInWithEmail({
      email,
    })
    const success = !!signInResponse?.ok
    setLoading(false)
    success
      ? setActiveStep('ConfirmationEmailSent')
      : setErrorMessage(t('accentro:auth.login.error'))
  }, [email, setActiveStep, t])

  const methods = useForm<LoginFormData>()
  const { handleSubmit } = methods

  return (
    <FormProvider {...methods}>
      <StyledForm onSubmit={handleSubmit(onSubmit)}>
        <AuthEmailDisplay />
        {!!errorMessage && <SimpleFieldError>{errorMessage}</SimpleFieldError>}
        <div className="flex flex-row flex-wrap gap-px-16">
          <SimpleMuiButton
            variant="contained"
            type="submit"
            color="primary"
            loading={loading}
            disabled={loading}
          >
            {t('accentro:auth.requestLoginLink')}
          </SimpleMuiButton>
          <SimpleMuiButton
            variant="outlined"
            type="button"
            onClick={() => setActiveStep('loginWithPassword')}
          >
            {t('accentro:auth.enterPassword')}
          </SimpleMuiButton>
        </div>
      </StyledForm>
    </FormProvider>
  )
}
